<template>
  <div>
    <mf-tooltip bottom max-width="300px">
      <template v-slot:activator="{ on }">
        <div class="toolbar-custom-btn mx-2" v-on="on" @click="openDrawer">
          <v-badge v-if="showBadge" color="vermelho-0" overlap>
            <mf-icon color="branco" icon="forward_to_inbox" />
          </v-badge>
          <mf-icon v-else color="branco" icon="forward_to_inbox" :wght="300" />
        </div>
      </template>
      <span>Histórico de exportações</span>
    </mf-tooltip>
    <v-navigation-drawer v-model="drawer" hide-overlay absolute right class="export-report-tracking-drawer" temporary width="450">
      <h1 class="pb-2 mb-0">Histórico de exportações</h1>

      <DynamicScroller
        v-if="items.length"
        v-slot="{ item, index, active }"
        class="vue-recycle-scroller scroller direction-vertical"
        :items="formattedItemsByDate"
        :min-item-size="103"
        key-field="id"
        @scroll-end="loadMore"
      >
        <DynamicScrollerItem :item="item" :active="active" :data-index="index">
          <h2 v-if="index === 0 || (formattedItemsByDate[index - 1] && item.day !== formattedItemsByDate[index - 1].day)" class="py-2 mb-0">{{ item.day }}</h2>
          <report-card :report="item" :allow-resend-report="isAllowedToResend(item)" @resend-report="resendReport" />
        </DynamicScrollerItem>
      </DynamicScroller>

      <div v-else class="export-report-empty-state">
        <div class="icon-container">
          <mf-icon fill size="24" color="cinza-l10" icon="forward_to_inbox" />
        </div>
        <h2>Nenhum relatório encontrado</h2>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import ReportCard from './ReportCard.vue'
import { wsClient } from '@/services/webSocket'
import { SUBSCRIPTION_ASYNC_JOBS_REPORTS, QUERY_ASYNC_JOBS, MUTATION_RESEND_ASYNC_JOB } from './graphql'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import { DateTime } from 'luxon'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

export default {
  name: 'ReportExportTracking',
  components: { ReportCard, DynamicScroller, DynamicScrollerItem },
  data: () => ({
    items: [],
    loading: false,
    limit: 5,
    cursor: '',
    drawer: false,
    showBadge: false,
  }),
  computed: {
    formattedItemsByDate() {
      const formattedCreatedAtItems = this.items.map((item) => {
        return {
          ...item,
          day: DateTime.fromISO(item.createdAt.toString()).toFormat('dd/MM'),
        }
      })
      return formattedCreatedAtItems
    },
  },
  watch: {
    items(newValue) {
      let hasEnqueued = false
      newValue.map((item) => {
        if (item.name === 'cashback.usercashback.adjustment.report' && item.state === 'ENQUEUED') {
          hasEnqueued = true
        }
      })
      this.$store.dispatch('setEnqueuedCashbackAdjustmentReportState', hasEnqueued)
    },
  },
  async mounted() {
    this.getData()
    const self = this
    await wsClient.subscribe(
      {
        query: SUBSCRIPTION_ASYNC_JOBS_REPORTS,
        operationName: 'AsyncJobsReports',
      },
      {
        next({ data: { asyncJobsReports } }) {
          if (!self.drawer) self.showBadge = true
          const index = self.items.findIndex((item) => item.id === asyncJobsReports.id)
          if (index >= 0) self.items.splice(index, 1)
          self.items.unshift(asyncJobsReports)
        },
        error(error) {
          console.error(error)
        },
      }
    )
  },
  methods: {
    isAllowedToResend(item) {
      return item.name !== 'cashback.usercashback.adjustment.report' && item.state !== 'FAILED'
    },
    openDrawer() {
      this.drawer = true
      this.showBadge = false
    },
    async getData() {
      try {
        this.loading = true
        const {
          data: {
            asyncJobs: { asyncJobs, cursor },
          },
        } = await this.$apollo.query({
          query: QUERY_ASYNC_JOBS,
          variables: {
            cursor: this.cursor || '',
            limit: this.limit,
          },
          fetchPolicy: 'no-cache',
        })
        this.items = this.items.concat(asyncJobs)
        this.cursor = cursor
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      if (!this.loading && this.cursor !== null) {
        this.getData()
      }
    },
    async resendReport(id) {
      const newReportsArray = this.items.filter((report) => report.id !== id)
      this.items = newReportsArray

      try {
        await this.$apollo.mutate({
          mutation: MUTATION_RESEND_ASYNC_JOB,
          variables: { id },
        })
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Ocorreu um erro ao reenviar o relatório.', snackbarColor: 'error' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.export-report-tracking-drawer {
  margin-top: 64px;
}

::v-deep aside {
  height: unset !important;
  box-shadow: -6px 0px 12px 0px rgba(0, 0, 0, 0.1) !important;
}

::v-deep .v-navigation-drawer__content {
  height: calc(100vh - 64px);
  background-color: $branco;
  padding: 16px 12px 0 20px;

  h1 {
    font-size: 18px;
    color: $cinza-d10;
    line-height: 120%;
    font-weight: 700;
  }

  h2 {
    font-size: 16px;
    color: $cinza-d10;
    line-height: 150%;
    font-weight: 400;
  }
}

::v-deep .v-badge__badge {
  height: 12px;
  min-width: 12px;
  inset: auto auto calc(100% - 10px) calc(100% - 8px) !important;
}

.scroller {
  height: calc(100vh - 120px) !important;
  overflow-y: auto !important;
}

.export-report-empty-state {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 8px;
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23A5ACB4FF' stroke-width='1' stroke-dasharray='10' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
  border-radius: 4px;
  margin-top: 16px;

  & .icon-container {
    width: 44px;
    height: 44px;
    background: $cinza-l90;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  & h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 400;
    color: $cinza-l10;
  }
}
</style>
